import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { projectsApi, knowledgeBasesApi } from '../utils/api';
import type { Project } from '../types/project';
import type { KnowledgeBaseListItem } from '../types/knowledgeBase';
import Navigation from '../components/Navigation';

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [availableKBs, setAvailableKBs] = useState<KnowledgeBaseListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [selectedKBs, setSelectedKBs] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const loadProject = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      const data = await projectsApi.getById(parseInt(id));
      setProject(data);
      setEditName(data.name);
      setEditDescription(data.description || '');
      setSelectedKBs(data.knowledge_bases.map(kb => kb.id));
      setError('');
    } catch (err) {
      setError('Failed to load project');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailableKBs = async () => {
    try {
      const kbs = await knowledgeBasesApi.getAll(false);
      setAvailableKBs(kbs);
    } catch (err) {
      console.error('Failed to load knowledge bases:', err);
    }
  };

  useEffect(() => {
    loadProject();
    loadAvailableKBs();
  }, [id]);

  const handleKBToggle = (kbId: number) => {
    setSelectedKBs(prev =>
      prev.includes(kbId)
        ? prev.filter(id => id !== kbId)
        : [...prev, kbId]
    );
  };

  const handleSaveEdit = async () => {
    if (!id || !editName.trim()) return;

    try {
      setIsSaving(true);
      await projectsApi.update(parseInt(id), {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
        knowledge_base_ids: selectedKBs.length > 0 ? selectedKBs : undefined,
      });
      setIsEditing(false);
      await loadProject();
    } catch (err) {
      console.error('Failed to update project:', err);
      alert('Failed to update project');
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!id || !confirm('Are you sure you want to archive this project?')) return;

    try {
      await projectsApi.archive(parseInt(id));
      await loadProject();
    } catch (err) {
      console.error('Failed to archive project:', err);
      alert('Failed to archive project');
    }
  };

  const handleDelete = async () => {
    if (!id || !confirm('Are you sure you want to delete this project? This action cannot be undone.')) return;

    try {
      await projectsApi.delete(parseInt(id));
      navigate('/projects');
    } catch (err: any) {
      console.error('Failed to delete project:', err);
      alert(err.response?.data?.detail || 'Failed to delete project');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{error || 'Project not found'}</p>
          <Link to="/projects" className="mt-4 inline-block text-blue-600">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{project.name} - MAIA Agent 128</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <Navigation />

      <div className="max-w-6xl mx-auto px-4 pt-24 pb-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1">
              {isEditing ? (
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="text-3xl font-bold border-b-2 border-gray-300 focus:border-black outline-none"
                />
              ) : (
                <div className="flex items-center space-x-4">
                  <h1 className="text-3xl font-bold">{project.name}</h1>
                  {project.archived && (
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
                      Archived
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {isEditing ? (
                <>
                  <button
                    onClick={handleSaveEdit}
                    disabled={isSaving}
                    className="px-4 py-2 btn-primary rounded-md text-sm"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setEditName(project.name);
                      setEditDescription(project.description || '');
                      setSelectedKBs(project.knowledge_bases.map(kb => kb.id));
                    }}
                    className="px-4 py-2 btn-secondary rounded-md text-sm"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 btn-secondary rounded-md text-sm"
                  >
                    Edit
                  </button>
                  {!project.archived && (
                    <button
                      onClick={handleArchive}
                      className="px-4 py-2 btn-secondary rounded-md text-sm"
                    >
                      Archive
                    </button>
                  )}
                  <button
                    onClick={handleDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700"
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>

          {isEditing ? (
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-200 rounded-md"
              placeholder="Description (optional)"
            />
          ) : (
            project.description && (
              <p className="text-gray-600">{project.description}</p>
            )
          )}
        </div>

        {/* Knowledge Bases Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-medium mb-4">
            Knowledge Bases ({project.knowledge_bases.length})
          </h2>

          {isEditing ? (
            <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-md p-3">
              {availableKBs.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No knowledge bases available</p>
              ) : (
                availableKBs.map(kb => (
                  <label key={kb.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={selectedKBs.includes(kb.id)}
                      onChange={() => handleKBToggle(kb.id)}
                      className="rounded border-gray-300"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium">{kb.title}</span>
                      {kb.description && (
                        <p className="text-xs text-gray-500 line-clamp-1">{kb.description}</p>
                      )}
                    </div>
                    <span className="text-xs text-gray-400">{kb.resource_count} resources</span>
                  </label>
                ))
              )}
            </div>
          ) : (
            <>
              {project.knowledge_bases.length === 0 ? (
                <p className="text-gray-500">No knowledge bases associated with this project</p>
              ) : (
                <div className="space-y-2">
                  {project.knowledge_bases.map((kb) => (
                    <Link
                      key={kb.id}
                      to={`/knowledge-bases/${kb.id}`}
                      className="block p-4 border border-gray-200 rounded-md hover:bg-gray-50"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{kb.title}</p>
                          <p className="text-sm text-gray-500">{kb.resource_count} resources</p>
                        </div>
                        {kb.is_default && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                            Default
                          </span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-xl font-medium mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-4">
            <Link
              to={`/tasks?project=${id}`}
              className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 text-center"
            >
              <p className="font-medium">View Tasks</p>
              <p className="text-sm text-gray-500 mt-1">See all tasks in this project</p>
            </Link>
            <Link
              to="/tasks/new"
              state={{ projectId: id ? parseInt(id) : undefined }}
              className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 text-center"
            >
              <p className="font-medium">Create Task</p>
              <p className="text-sm text-gray-500 mt-1">Add a new task to this project</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
